import axios from "axios";

function resolveBackendUrl() {
  const configured = (process.env.REACT_APP_BACKEND_URL || "").trim().replace(/\/$/, "");
  if (process.env.NODE_ENV === "development") {
    // Local dev: use CRA proxy via relative /api paths (avoids CORS on the backend port).
    if (!configured || /^https?:\/\/(localhost|127\.0\.0\.1):(5500|8000|8001)$/i.test(configured)) {
      return "";
    }
  }
  return configured;
}

export const BACKEND_URL = resolveBackendUrl();
export const API_BASE = BACKEND_URL ? `${BACKEND_URL}/api/v1` : "/api/v1";

export const api = axios.create({ baseURL: API_BASE, timeout: 30000 });

const TOKEN_KEY = "project_local_token";
const REFRESH_KEY = "project_local_refresh_token";

export function getStoredAccessToken() {
  return window.localStorage.getItem(TOKEN_KEY);
}

export function getStoredRefreshToken() {
  return window.localStorage.getItem(REFRESH_KEY);
}

export function storeSessionTokens({ token, refresh_token: refreshToken }) {
  if (token) window.localStorage.setItem(TOKEN_KEY, token);
  if (refreshToken) window.localStorage.setItem(REFRESH_KEY, refreshToken);
}

export function clearSessionTokens() {
  window.localStorage.removeItem(TOKEN_KEY);
  window.localStorage.removeItem(REFRESH_KEY);
}

api.interceptors.request.use((config) => {
  const token = getStoredAccessToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

let refreshPromise = null;

async function refreshAccessToken() {
  const refreshToken = getStoredRefreshToken();
  if (!refreshToken) throw new Error("No refresh token");
  const response = await axios.post(`${API_BASE}/auth/refresh`, { refresh_token: refreshToken }, { timeout: 15000 });
  const data = response.data?.data;
  if (!data?.token) throw new Error("Refresh failed");
  storeSessionTokens(data);
  return data.token;
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    const status = error.response?.status;
    const code = error.response?.data?.detail?.error?.code || error.response?.data?.error?.code;
    const isAuthFailure = status === 401 && (code === "INVALID_TOKEN" || code === "AUTH_REQUIRED" || !code);
    if (!original || original._retry || !isAuthFailure || original.url?.includes("/auth/")) {
      return Promise.reject(error);
    }
    original._retry = true;
    try {
      refreshPromise = refreshPromise || refreshAccessToken().finally(() => {
        refreshPromise = null;
      });
      const token = await refreshPromise;
      original.headers = original.headers || {};
      original.headers.Authorization = `Bearer ${token}`;
      return api(original);
    } catch {
      clearSessionTokens();
      return Promise.reject(error);
    }
  }
);

export async function apiGet(path) {
  const response = await api.get(path);
  return response.data.data;
}

export async function apiPost(path, payload) {
  const response = await api.post(path, payload);
  return response.data.data;
}

export async function apiPatch(path, payload) {
  const response = await api.patch(path, payload);
  return response.data.data;
}

export async function apiDelete(path) {
  const response = await api.delete(path);
  return response.data.data;
}

export async function uploadFile(file) {
  const contentType = String(file?.type || "").toLowerCase();
  if (contentType.startsWith("image/")) {
    const presigned = await apiPost("/uploads/presign", {
      filename: file.name,
      contentType,
    });
    const putResponse = await fetch(presigned.uploadUrl, {
      method: "PUT",
      headers: { "Content-Type": contentType || "application/octet-stream" },
      body: file,
    });
    if (!putResponse.ok) {
      throw new Error(`Direct upload failed (${putResponse.status})`);
    }
    return apiPost("/uploads/complete", {
      key: presigned.key,
      filename: file.name,
      contentType,
      size: file.size,
    });
  }

  const formData = new FormData();
  formData.append("file", file);
  const response = await api.post("/uploads", formData, {
    timeout: 120000,
  });
  return response.data.data;
}

export async function uploadMenuImport(file) {
  const formData = new FormData();
  formData.append("file", file);
  const response = await api.post("/products/import-draft", formData, {
    timeout: 120000,
  });
  return response.data.data;
}

/** Upload one or more menu files and merge draft products into a single review session. */
export async function uploadMenuImports(files) {
  const list = Array.from(files || []).filter(Boolean);
  if (!list.length) return null;

  const results = [];
  const failures = [];

  for (const file of list) {
    try {
      const result = await uploadMenuImport(file);
      results.push(result);
    } catch (error) {
      failures.push({
        filename: file.name,
        message: error.response?.data?.detail?.error?.message || error.message || "Import failed",
        code: error.response?.data?.detail?.error?.code,
      });
    }
  }

  if (!results.length) {
    const first = failures[0];
    const error = new Error(first?.message || "Could not parse menu files");
    error.response = { data: { detail: { error: { message: first?.message, code: first?.code } } } };
    error.failures = failures;
    throw error;
  }

  const draftProducts = results.flatMap((result) =>
    (result.draft_products || []).map((row) => ({
      ...row,
      source_filename: result.filename,
    }))
  );

  const primary = {
    ...results[0],
    filename: results.map((result) => result.filename).join(", "),
    draft_products: draftProducts,
    summary: {
      ...(results[0].summary || {}),
      draft_count: draftProducts.length,
      warning_count: draftProducts.filter((row) => (row.warnings || []).length > 0).length,
      ocr_warning_count: draftProducts.filter((row) => (row.warnings || []).some((warning) => String(warning).toLowerCase().includes("ocr"))).length,
      source_file_count: results.length,
      failed_file_count: failures.length,
      source_filenames: results.map((result) => result.filename),
      failed_filenames: failures.map((item) => item.filename),
    },
  };

  if (results.length > 1 || draftProducts.length !== (results[0].draft_products || []).length) {
    const updated = await apiPatch(`/products/import-draft/${primary.id}`, { draft_products: draftProducts });
    return {
      ...updated,
      filename: primary.filename,
      summary: {
        ...(updated.summary || {}),
        source_file_count: results.length,
        failed_file_count: failures.length,
        source_filenames: primary.summary.source_filenames,
        failed_filenames: primary.summary.failed_filenames,
      },
      _failures: failures,
    };
  }

  return { ...primary, _failures: failures };
}

export async function uploadLegalDocument(docType, file) {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("doc_type", docType);
  const response = await api.post("/legal-documents/upload", formData, {
    timeout: 180000,
  });
  return response.data.data;
}
